import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Clock,
  DollarSign,
  Package,
  CheckCircle,
  Calendar as CalendarIcon,
} from 'lucide-react';

import { demurrageApi, DemurrageStatus } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { useCountdown } from '@/hooks/useCountdown';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// ==================== Types & Schemas ====================

const markReturnedSchema = z.object({
  returnDate: z.date({
    required_error: 'Return date is required',
  }),
  returnTime: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:mm)'),
  notes: z.string().optional(),
});

type MarkReturnedFormData = z.infer<typeof markReturnedSchema>;

interface DemurragePanelProps {
  shipmentId: string;
  className?: string;
}

// ==================== Alert Color Configurations ====================

const alertColors = {
  CRITICAL: {
    bg: 'bg-status-destructive-subtle',
    text: 'text-status-destructive',
    border: 'border-status-destructive-border',
    icon: 'text-status-destructive',
  },
  URGENT: {
    bg: 'bg-status-warning-subtle',
    text: 'text-status-warning',
    border: 'border-status-warning-border',
    icon: 'text-status-warning',
  },
  EXPIRING: {
    bg: 'bg-status-warning-subtle',
    text: 'text-status-warning-foreground',
    border: 'border-status-warning-border',
    icon: 'text-status-warning-foreground',
  },
  NORMAL: {
    bg: 'bg-status-success-subtle',
    text: 'text-status-success',
    border: 'border-status-success-border',
    icon: 'text-status-success',
  },
};

// ==================== Status Badge Configuration ====================

const statusConfig: Record<DemurrageStatus, { label: string; icon: string; variant: string }> = {
  WITHIN_FREE_TIME: { label: 'Within Free Time', icon: '🟢', variant: 'success' },
  FREE_TIME_EXPIRING: { label: 'Free Time Expiring', icon: '🟡', variant: 'warning' },
  DEMURRAGE_ACCRUING: { label: 'Demurrage Accruing', icon: '🔴', variant: 'destructive' },
  CONTAINER_RETURNED: { label: 'Container Returned', icon: '🔵', variant: 'secondary' },
  WAIVED: { label: 'Waived', icon: '⚪', variant: 'outline' },
};

// ==================== Main Component ====================

export function DemurragePanel({ shipmentId, className }: DemurragePanelProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // ==================== Data Fetching ====================

  const {
    data: demurrageData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['demurrage', shipmentId],
    queryFn: () => demurrageApi.calculateDemurrage(shipmentId),
    enabled: !!shipmentId,
    refetchInterval: 300000, // Refresh from server every 5 minutes
    staleTime: 60000, // Consider data fresh for 1 minute
  });

  // ==================== Countdown Timer ====================

  const countdown = useCountdown(demurrageData?.freeTimeExpiry || null, {
    onExpire: () => {
      toast.warning('Free time has expired! Demurrage charges are now accruing.');
      refetch();
    },
    updateInterval: 1000,
  });

  // ==================== Mark Returned Mutation ====================

  const form = useForm<MarkReturnedFormData>({
    resolver: zodResolver(markReturnedSchema),
    defaultValues: {
      returnDate: new Date(),
      returnTime: format(new Date(), 'HH:mm'),
      notes: '',
    },
  });

  const markReturnedMutation = useMutation({
    mutationFn: (data: MarkReturnedFormData) => {
      // Combine date and time
      const [hours, minutes] = data.returnTime.split(':').map(Number);
      const returnDateTime = new Date(data.returnDate);
      returnDateTime.setHours(hours, minutes, 0, 0);

      return demurrageApi.markContainerReturned(shipmentId, {
        returnDate: returnDateTime,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['demurrage', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['tracking-shipment', shipmentId] });
      toast.success(
        `Container marked as returned. Final charges: $${response.shipment.totalDemurrageCharges.toFixed(2)}`
      );
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  // ==================== Helper Functions ====================

  const getProgressPercentage = (): number => {
    if (!demurrageData?.freeTimeDays || countdown.isExpired) return 100;
    const totalHoursInFreeDays = demurrageData.freeTimeDays * 24;
    const hoursUsed = totalHoursInFreeDays - countdown.totalHoursRemaining;
    return Math.min(100, Math.max(0, (hoursUsed / totalHoursInFreeDays) * 100));
  };

  const getProjectedCharge = (): number => {
    if (!demurrageData) return 0;
    const { demurrageDaysAccrued, demurrageRatePerDay } = demurrageData;
    return (demurrageDaysAccrued + 1) * demurrageRatePerDay;
  };

  const shouldShowAlert = (): boolean => {
    return (
      demurrageData?.status === 'FREE_TIME_EXPIRING' ||
      demurrageData?.status === 'DEMURRAGE_ACCRUING'
    );
  };

  const isActionDisabled = (): boolean => {
    return demurrageData?.status === 'CONTAINER_RETURNED' || demurrageData?.status === 'WAIVED';
  };

  // ==================== Loading State ====================

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-8 bg-muted rounded w-1/2" />
              <div className="h-4 bg-muted rounded w-3/4" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== Error State ====================

  if (error || !demurrageData) {
    return (
      <div className={cn('space-y-4', className)}>
        <Card>
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
              <div>
                <h3 className="text-base font-semibold">Failed to Load Demurrage Data</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {error instanceof Error ? error.message : 'An unknown error occurred'}
                </p>
              </div>
              <Button onClick={() => refetch()} variant="outline">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== Main Render ====================

  return (
    <div className={cn('space-y-4', className)}>
      {/* Alert Banner */}
      {shouldShowAlert() && (
        <div
          className={cn(
            'rounded-lg border p-4 flex items-center gap-3',
            alertColors[countdown.alertLevel].bg,
            alertColors[countdown.alertLevel].border
          )}
        >
          <AlertTriangle className={cn('h-5 w-5', alertColors[countdown.alertLevel].icon)} />
          <div className={cn('flex-1 font-medium', alertColors[countdown.alertLevel].text)}>
            {countdown.isExpired ? (
              <>Demurrage charges are accruing! Container must be returned immediately.</>
            ) : (
              <>
                {countdown.alertLevel === 'CRITICAL' && 'CRITICAL ALERT: '}
                {countdown.alertLevel === 'URGENT' && 'URGENT: '}
                Free Time Expires in {countdown.days > 0 && `${countdown.days}d `}
                {countdown.hours}h {countdown.minutes}m
              </>
            )}
          </div>
        </div>
      )}

      {/* Card 1: Free Time Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              <Clock className="h-5 w-5" />
              Free Time Status
            </h3>
            <Badge variant={statusConfig[demurrageData.status].variant as any}>
              {statusConfig[demurrageData.status].icon} {statusConfig[demurrageData.status].label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Last Free Day */}
          <div>
            <p className="text-sm text-muted-foreground">Last Free Day</p>
            <p className="text-lg font-semibold">
              {demurrageData.freeTimeExpiry
                ? format(demurrageData.freeTimeExpiry, 'MMM d, yyyy HH:mm')
                : 'Not configured'}
            </p>
          </div>

          {/* Countdown */}
          {demurrageData.freeTimeExpiry && !countdown.isExpired && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Time Remaining
              </p>
              <div className="flex items-baseline gap-2">
                <span className={cn('text-4xl font-bold', alertColors[countdown.alertLevel].text)}>
                  {countdown.days}
                </span>
                <span className="text-sm text-muted-foreground">d</span>
                <span className={cn('text-4xl font-bold', alertColors[countdown.alertLevel].text)}>
                  {countdown.hours}
                </span>
                <span className="text-sm text-muted-foreground">h</span>
                <span
                  className={cn('text-2xl font-semibold', alertColors[countdown.alertLevel].text)}
                >
                  {countdown.minutes}
                </span>
                <span className="text-sm text-muted-foreground">m</span>
                <span
                  className={cn('text-2xl font-semibold', alertColors[countdown.alertLevel].text)}
                >
                  {countdown.seconds}
                </span>
                <span className="text-sm text-muted-foreground">s</span>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {demurrageData.freeTimeExpiry && (
            <div className="space-y-2">
              <Progress value={getProgressPercentage()} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {getProgressPercentage().toFixed(1)}% time used
                {!countdown.isExpired &&
                  ` (${(countdown.totalHoursRemaining / 24).toFixed(1)} of ${demurrageData.freeTimeDays} days remaining)`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card 2: Demurrage Charges */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-foreground">
            <DollarSign className="h-5 w-5" />
            Demurrage Charges
          </h3>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Rate</p>
              <p className="text-base font-medium">
                ${demurrageData.demurrageRatePerDay?.toFixed(2) || '0.00'}/day
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Days Accrued</p>
              <p className="text-base font-medium">{demurrageData.demurrageDaysAccrued} days</p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-1">Current Total</p>
            <p className="text-3xl font-bold">${demurrageData.totalDemurrageCharges.toFixed(2)}</p>
          </div>

          {demurrageData.status !== 'CONTAINER_RETURNED' &&
            demurrageData.status !== 'WAIVED' &&
            countdown.isExpired && (
              <div className="text-sm text-muted-foreground">
                Projected (if not returned by {format(addDays(new Date(), 1), 'MMM d')}): $
                {getProjectedCharge().toFixed(2)}
              </div>
            )}

          <div className="text-xs text-muted-foreground pt-2">
            Last Updated: {format(new Date(), 'yyyy-MM-dd HH:mm')} UTC
          </div>
        </CardContent>
      </Card>

      {/* Card 3: Actions */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-foreground">Actions</h3>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            className="w-full"
            onClick={() => setIsDialogOpen(true)}
            disabled={isActionDisabled()}
          >
            <Package className="mr-2 h-4 w-4" />
            Mark Container Returned
          </Button>
          <Button variant="outline" className="w-full" disabled>
            <CalendarIcon className="mr-2 h-4 w-4" />
            Request Free Time Extension
          </Button>
          <Button variant="outline" className="w-full" disabled>
            <AlertTriangle className="mr-2 h-4 w-4" />
            Dispute Charges
          </Button>
        </CardContent>
      </Card>

      {/* Mark Returned Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Container Returned</DialogTitle>
            <DialogDescription>
              Enter the date and time when the container was returned. This will finalize demurrage
              charges and stop further accrual.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => markReturnedMutation.mutate(data))}
              className="space-y-4"
            >
              {/* Return Date */}
              <FormField
                control={form.control}
                name="returnDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Return Date *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Return Time */}
              <FormField
                control={form.control}
                name="returnTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Return Time *</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any relevant notes..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="bg-status-warning-subtle border border-status-warning-border rounded-lg p-3 flex gap-2">
                <AlertTriangle className="h-5 w-5 text-status-warning flex-shrink-0 mt-0.5" />
                <p className="text-sm text-status-warning">
                  This will finalize demurrage charges and stop further accrual.
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={markReturnedMutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={markReturnedMutation.isPending}>
                  {markReturnedMutation.isPending ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Confirm Return
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
